import { supabase } from './supabase.js';

let _cache = null;

export async function fetchCatalog() {
  if (_cache) return _cache;

  const [catsRes, prodsRes, variantsRes, bundlesRes] = await Promise.all([
    supabase.from('categories').select('*').order('sort_order', { ascending: true }),
    supabase.from('products').select('*').order('id', { ascending: true }),
    supabase.from('product_variants').select('*').order('sort_order', { ascending: true }),
    supabase.from('bundle_items').select('*').order('sort_order', { ascending: true }),
  ]);

  if (catsRes.error) throw catsRes.error;
  if (prodsRes.error) throw prodsRes.error;
  // variants / bundle_items tables may not exist on installs that
  // haven't run the migration yet — degrade gracefully instead of
  // failing the whole catalog.
  const variantRows = variantsRes.error ? [] : (variantsRes.data || []);
  const bundleRows  = bundlesRes.error ? [] : (bundlesRes.data || []);

  // Reshape to match the existing in-memory format used by script.js
  const categoriesByName = new Map();
  const categories = catsRes.data.map(c => {
    const out = { id: c.id, name: c.name, image: c.image_url || null, count: 0 };
    categoriesByName.set(c.id, out);
    return out;
  });

  const variantsByProduct = new Map();
  for (const v of variantRows) {
    if (!variantsByProduct.has(v.product_id)) variantsByProduct.set(v.product_id, []);
    variantsByProduct.get(v.product_id).push({
      id: v.id,
      productId: v.product_id,
      label: v.label,
      mrp: v.mrp != null ? Number(v.mrp) : null,
      salePrice: Number(v.sale_price),
      stockQty: v.stock_qty != null ? Number(v.stock_qty) : 0,
      lowStockThreshold: v.low_stock_threshold ?? 5,
      mainImage: v.main_image_url || null,
      gallery: Array.isArray(v.gallery_image_urls) ? v.gallery_image_urls.filter(Boolean) : [],
      sortOrder: v.sort_order ?? 0,
    });
  }

  const bundleItemsByBundle = new Map();
  for (const b of bundleRows) {
    if (!bundleItemsByBundle.has(b.bundle_product_id)) bundleItemsByBundle.set(b.bundle_product_id, []);
    bundleItemsByBundle.get(b.bundle_product_id).push({
      id: b.id,
      memberProductId: b.member_product_id,
      memberVariantId: b.member_variant_id || null,
      quantity: b.quantity || 1,
      sortOrder: b.sort_order ?? 0,
    });
  }

  const products = prodsRes.data.map(p => {
    const cat = categoriesByName.get(p.category_id);
    if (cat) cat.count += 1;
    const gallery = Array.isArray(p.gallery_image_urls) ? p.gallery_image_urls.filter(Boolean) : [];
    const stockQty = p.stock_qty != null ? Number(p.stock_qty) : null;
    const variants = variantsByProduct.get(p.id) || [];
    const hasVariants = !!p.has_variants && variants.length > 0;

    // For variant products, derive the storefront-card "from ₹X" price and
    // total stock from the cheapest / aggregated variant data so the listing
    // grid can render without picking a specific variant.
    const variantMinPrice = hasVariants
      ? Math.min(...variants.map(v => v.salePrice))
      : null;
    const variantMaxMrp = hasVariants
      ? Math.max(...variants.map(v => v.mrp ?? v.salePrice))
      : null;
    const variantTotalStock = hasVariants
      ? variants.reduce((s, v) => s + (v.stockQty || 0), 0)
      : null;

    return {
      id: p.id,
      categoryId: p.category_id,
      category: cat ? cat.name : '',
      name: p.name,
      subtitle: p.subtitle,
      shortDescription: p.short_description || '',
      description: p.description,
      howToUse: p.how_to_use || '',
      // Prefer modern fields, fall back to legacy.
      price: hasVariants ? variantMinPrice : Number(p.sale_price ?? p.price),
      original: hasVariants
        ? variantMaxMrp
        : Number(p.mrp ?? p.original_price ?? p.sale_price ?? p.price),
      weight: p.weight,
      rating: Number(p.rating ?? 0),
      reviews: p.reviews ?? 0,
      badge: p.badge,
      img: p.img_key,
      photo: hasVariants
        ? (variants[0].mainImage || p.main_image_url || p.photo)
        : (p.main_image_url || p.photo),
      photos: gallery.length ? gallery : p.photos,
      stockQty: hasVariants ? variantTotalStock : stockQty,
      lowStockThreshold: p.low_stock_threshold ?? 5,
      inStock: hasVariants
        ? variantTotalStock > 0
        : (stockQty != null ? stockQty > 0 : !!p.in_stock),
      tags: Array.isArray(p.tags) ? p.tags.filter(Boolean) : [],
      slug: p.slug || null,
      metaTitle: p.meta_title || null,
      metaDescription: p.meta_description || null,
      hasVariants,
      variants,
      freeShipping: !!p.free_shipping,
      isBundle: !!p.is_bundle,
      bundleItems: bundleItemsByBundle.get(p.id) || [],
    };
  });

  _cache = { categories, products };
  return _cache;
}

export function clearCatalogCache() { _cache = null; }
